import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { ICampaignDesign, IMailingCampaignItemList, ICampaign, ICampaignClone, ICampaignForList, CampaignsPagedData, CampaignsPage } from './campaigns.model';

@Injectable()
export class CampaignsService {
    constructor(
        private http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT = 'campaigns';

    /** GET */
    public getMailingCampaigns(): Observable<SuccessResponse<IMailingCampaignItemList> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IMailingCampaignItemList>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/dropdowns' )
            .pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
      }

    public getCampaign(id: string): Observable<SuccessResponse<ICampaign> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<ICampaign>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/' + id,
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** PUT */
    public updateDesign(data: ICampaignDesign): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/' + data?.campaignId +
                '/designs',
                this.convertToFormData(data)
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
      }

    public updateCampaign(data: ICampaign): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .put<SuccessResponse<boolean>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/' + data?.id,
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

     /** POST */
     public addCampaign(data: ICampaign): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT,
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public cloneCampaign(data: ICampaignClone): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/' + data?.id + '/clones',
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getCampaigns(page: CampaignsPage): Observable<SuccessResponse<CampaignsPagedData<ICampaignForList>> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<CampaignsPagedData<ICampaignForList>>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/filters',
                page
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** DELETE */
    public softDeleteCampaign(id: string): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/' + id + '/softdeletes',
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public deleteCampaign(id: string): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                CampaignsService.URL +
                CampaignsService.ENDPOINT + '/' + id,
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    private convertToFormData(data: ICampaignDesign): FormData {
        const formData: FormData = new FormData();

        // Remove empty files
        const banners = [];
        data.bannerContents?.forEach(content => {
            if (!content.isRemoved && (content.file || content.id)) {
                banners.push(content);
            }
        });
        data.bannerContents = [...banners];

        // Top level
        formData.append('advancedTemplateId', data.advancedTemplateId);
        formData.append('campaignId', data.campaignId);

        // Banner
        data.bannerContents.forEach((fileData, index) => {
            formData.append(`bannerContents[${index}].id`, fileData.id ?? '');
            formData.append(`bannerContents[${index}].canClone`, JSON.stringify(fileData.canClone));
            formData.append(`bannerContents[${index}].languageId`, fileData.languageId);
            formData.append(`bannerContents[${index}].contentMediaType`, JSON.stringify(fileData.contentMediaType));
            formData.append(`bannerContents[${index}].contentContextType`, JSON.stringify(fileData.contentContextType));
            formData.append(`bannerContents[${index}].file`, fileData.file);
        });

        // Footers
        data.footers.forEach((footer, index) => {
            formData.append(`footers[${index}].index`, JSON.stringify(footer.index));
            formData.append(`footers[${index}].footerId`, footer.footerId);
            formData.append(`footers[${index}].contentContextType`, JSON.stringify(footer.contentContextType));
        });

        return formData;
    }

}
